import LayoutMaster from "components/layout/layout_master";
import { MaxButton, StatusMaxButton } from "components/view/button";
import {
  CheckWithPrefix,
  CheckWithPrefixResponse,
} from "lib/infomation_vender/document";
import { AppReducerStateProps } from "lib/store";
import paymentV2GetDepositWithdrawHistory, {
  DepositWithdrawHistoryItem,
  GetDepositWithdrawHistoryRequest,
} from "lib/worker/api/payment_v2_get_deposit_withdraw_history";
import { prefix } from "lib/worker/client";
import { GetServerSideProps } from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

interface TransactionTransferV2Props {
  state: AppReducerStateProps;
  isLogin: boolean;
  infomation: CheckWithPrefixResponse;
}

function TransactionTransferV2(props: TransactionTransferV2Props) {
  const router = useRouter();
  const [transactions, setTransactions] = useState<
    DepositWithdrawHistoryItem[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [limit] = useState<number>(20);
  const [offset, setOffset] = useState<number>(0);
  const [lineid, setlineid] = useState<string | null | undefined>(null);

  const loadTransactions = useCallback(
    async (reset: boolean = false) => {
      if (loading) return;

      setLoading(true);
      const currentOffset = reset ? 0 : offset;

      try {
        const request: GetDepositWithdrawHistoryRequest = {
          limit: limit,
          offset: currentOffset,
        };

        const response = await paymentV2GetDepositWithdrawHistory(request);

        if (reset) {
          setTransactions(response.transactions || []);
          setOffset(0);
        } else {
          setTransactions((prev) => [
            ...prev,
            ...(response.transactions || []),
          ]);
        }

        setTotal(response.total || 0);
        setOffset(currentOffset + limit);
      } catch (error) {
        console.error("Error loading transactions:", error);
      } finally {
        setLoading(false);
      }
    },
    [loading, offset, limit]
  );

  useEffect(() => {
    const infomations = CheckWithPrefix({ host: props.infomation.domain });
    setlineid(infomations.lineidchat);
  }, [props.infomation.domain]);

  useEffect(() => {
    if (props.isLogin) {
      loadTransactions(true);
    }
  }, [props.isLogin, loadTransactions]);

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusText = (status?: string): string => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "success":
        return "สำเร็จ";
      case "approved":
        return "อนุมัติ";
      case "pending":
        return "กำลังดำเนินการ";
      case "rejected":
      case "failed":
      case "cancelled":
        return "ยกเลิก";
      default:
        return status || "ไม่ทราบสถานะ";
    }
  };

  const getStatusColor = (status?: string): string => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "success":
      case "approved":
        return "#29c94d";
      case "pending":
        return "#ffbe00";
      case "rejected":
      case "failed":
      case "cancelled":
        return "#ff3434";
      default:
        return "#cccccc";
    }
  };

  const getAmountColor = (transaction: DepositWithdrawHistoryItem): string => {
    if (
      transaction.status?.toLowerCase() === "rejected" ||
      transaction.status?.toLowerCase() === "failed" ||
      transaction.status?.toLowerCase() === "cancelled"
    ) {
      return "#cccccc";
    }

    if (transaction.transaction_type === "deposit") {
      return "#29c94d";
    } else {
      return "#ff3434";
    }
  };

  const formatAmount = (amount?: number, transaction_type?: string): string => {
    if (!amount) return "0.00";
    const formatted = Math.abs(amount).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    if (transaction_type === "deposit") {
      return `+${formatted}`;
    } else {
      return `-${formatted}`;
    }
  };

  // คำนวณสถิติ
  const getTotalStats = () => {
    const totalDeposit = transactions
      .filter(
        (t) =>
          t.transaction_type === "deposit" &&
          (t.status?.toLowerCase() === "completed" ||
            t.status?.toLowerCase() === "success" ||
            t.status?.toLowerCase() === "approved")
      )
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const totalWithdraw = transactions
      .filter(
        (t) =>
          t.transaction_type === "withdraw" &&
          (t.status?.toLowerCase() === "completed" ||
            t.status?.toLowerCase() === "success" ||
            t.status?.toLowerCase() === "approved")
      )
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const pendingCount = transactions.filter(
      (t) => t.status?.toLowerCase() === "pending"
    ).length;

    return { totalDeposit, totalWithdraw, pendingCount };
  };

  const { totalDeposit, totalWithdraw, pendingCount } = getTotalStats();

  return (
    <LayoutMaster
      content_setting={{
        title: "ประวัติ (ฝาก - ถอน)",
        description:
          "ตรวจสอบ ประวัติธุรกรรม ของท่านได้ตลอด ทุกเวลา สะดวกรวดเร็ว",
      }}
      information={props.infomation}
    >
      <div className="children-top-margin-top-10">
        {/* Header with gradient background */}
        <div
          className="text-center"
          style={{
            margin: "0 15px",
            borderRadius: "15px",
            padding: "20px 15px",
            marginBottom: "20px",
          }}
        >
          <h1
            style={{
              color: "#ffffff",
              margin: 0,
              fontSize: "24px",
              fontWeight: "bold",
            }}
          >
            ประวัติการทำรายการ
          </h1>
          <p
            style={{
              color: "#ffffff",
              margin: "5px 0 0 0",
              fontSize: "14px",
              opacity: 0.9,
            }}
          >
            ตรวจสอบธุรกรรมทั้งหมดของคุณ
          </p>
        </div>

        {/* สถิติสรุป - Enhanced */}
        {transactions.length > 0 && (
          <div className="container" style={{ paddingBottom: 20 }}>
            <div className="row" style={{ margin: "0 -5px" }}>
              {/* ยอดฝาก */}
              <div className="col-4" style={{ padding: "0 5px" }}>
                <div
                  className="card"
                  style={{
                    padding: "15px 10px",
                    background:
                      "linear-gradient(135deg, #29c94d22 0%, #29c94d11 100%)",
                    border: "1px solid #29c94d33",
                    borderRadius: "12px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      color: "#29c94d",
                      fontSize: "18px",
                      marginBottom: "5px",
                      fontWeight: "bold",
                    }}
                  >
                    ฝาก
                  </div>
                  <div
                    style={{
                      color: "#29c94d",
                      fontSize: "16px",
                      fontWeight: "bold",
                      marginBottom: "5px",
                    }}
                  >
                    +
                    {totalDeposit.toLocaleString("en-US", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </div>
                  <div style={{ color: "#ffffff", fontSize: "11px" }}>
                    ยอดฝากทั้งหมด
                  </div>
                </div>
              </div>

              {/* ยอดถอน */}
              <div className="col-4" style={{ padding: "0 5px" }}>
                <div
                  className="card"
                  style={{
                    padding: "15px 10px",
                    background:
                      "linear-gradient(135deg, #ff343422 0%, #ff343411 100%)",
                    border: "1px solid #ff343433",
                    borderRadius: "12px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      color: "#ff3434",
                      fontSize: "18px",
                      marginBottom: "5px",
                      fontWeight: "bold",
                    }}
                  >
                    ถอน
                  </div>
                  <div
                    style={{
                      color: "#ff3434",
                      fontSize: "16px",
                      fontWeight: "bold",
                      marginBottom: "5px",
                    }}
                  >
                    -
                    {totalWithdraw.toLocaleString("en-US", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </div>
                  <div style={{ color: "#ffffff", fontSize: "11px" }}>
                    ยอดถอนทั้งหมด
                  </div>
                </div>
              </div>

              {/* รายการรอ */}
              <div className="col-4" style={{ padding: "0 5px" }}>
                <div
                  className="card"
                  style={{
                    padding: "15px 10px",
                    background:
                      "linear-gradient(135deg, #ffbe0022 0%, #ffbe0011 100%)",
                    border: "1px solid #ffbe0033",
                    borderRadius: "12px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      color: "#ffbe00",
                      fontSize: "18px",
                      marginBottom: "5px",
                      fontWeight: "bold",
                    }}
                  >
                    รอ
                  </div>
                  <div
                    style={{
                      color: "#ffbe00",
                      fontSize: "16px",
                      fontWeight: "bold",
                      marginBottom: "5px",
                    }}
                  >
                    {pendingCount}
                  </div>
                  <div style={{ color: "#ffffff", fontSize: "11px" }}>
                    รายการรอดำเนินการ
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* รายการธุรกรรม - Card Layout */}
        <div className="container">
          {transactions.length === 0 && !loading && (
            <div
              className="card text-center"
              style={{
                padding: "40px 20px",
                background: "linear-gradient(135deg, #181c38 0%, #232749 100%)",
                borderRadius: "15px",
              }}
            >
              <div
                style={{
                  fontSize: "18px",
                  marginBottom: "15px",
                  color: "#ffbe00",
                  fontWeight: "bold",
                }}
              >
                {props.isLogin ? "ไม่มีข้อมูล" : "ยังไม่เข้าสู่ระบบ"}
              </div>
              <h4 style={{ color: "#ffffff", marginBottom: "10px" }}>
                {props.isLogin ? "ไม่พบข้อมูลการทำรายการ" : "กรุณาเข้าสู่ระบบ"}
              </h4>
              <p style={{ color: "#cccccc", fontSize: "14px", margin: 0 }}>
                {props.isLogin
                  ? "ยังไม่มีประวัติการทำรายการในระบบ"
                  : "เข้าสู่ระบบเพื่อดูประวัติการทำรายการของคุณ"}
              </p>
            </div>
          )}

          {loading && transactions.length === 0 && (
            <div
              className="card text-center"
              style={{
                padding: "40px 20px",
                background: "linear-gradient(135deg, #181c38 0%, #232749 100%)",
                borderRadius: "15px",
              }}
            >
              <div
                className="spinner-border"
                style={{ color: "#ffbe00", marginBottom: "15px" }}
              ></div>
              <h4 style={{ color: "#ffffff" }}>กำลังโหลดข้อมูล...</h4>
            </div>
          )}

          {/* Transaction Cards */}
          {transactions.map((transaction, index) => (
            <div
              key={`${transaction.transaction_id}-${index}`}
              className="card"
              style={{
                marginBottom: "15px",
                background: "linear-gradient(135deg, #181c38 0%, #232749 100%)",
                border: `1px solid ${
                  transaction.transaction_type === "deposit"
                    ? "#29c94d33"
                    : "#ff343433"
                }`,
                borderRadius: "12px",
                padding: "15px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Transaction Type Indicator */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "4px",
                  height: "100%",
                  background:
                    transaction.transaction_type === "deposit"
                      ? "#29c94d"
                      : "#ff3434",
                }}
              />

              <div className="row align-items-center">
                {/* Icon & Type */}
                <div className="col-3">
                  <div className="text-center">
                    <div
                      style={{
                        fontSize: "16px",
                        marginBottom: "5px",
                        fontWeight: "bold",
                        color:
                          transaction.transaction_type === "deposit"
                            ? "#29c94d"
                            : "#ff3434",
                      }}
                    >
                      {transaction.transaction_type === "deposit"
                        ? "ฝาก"
                        : "ถอน"}
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div className="col-6">
                  <div
                    style={{
                      color: "#ffffff",
                      fontSize: "13px",
                      marginBottom: "3px",
                    }}
                  >
                    {formatDate(transaction.created_at)}
                  </div>
                  {transaction.bank_name && (
                    <div
                      style={{
                        color: "#cccccc",
                        fontSize: "11px",
                        marginBottom: "2px",
                      }}
                    >
                      ธนาคาร: {transaction.bank_name}
                    </div>
                  )}
                  {transaction.reference_id && (
                    <div style={{ color: "#cccccc", fontSize: "10px" }}>
                      อ้างอิง: {transaction.reference_id}
                    </div>
                  )}
                </div>

                {/* Amount & Status */}
                <div className="col-3 text-right">
                  <div
                    style={{
                      color: getAmountColor(transaction),
                      fontSize: "14px",
                      fontWeight: "bold",
                      marginBottom: "5px",
                    }}
                  >
                    {formatAmount(
                      transaction.amount,
                      transaction.transaction_type
                    )}
                  </div>
                  <div
                    style={{
                      color: getStatusColor(transaction.status),
                      fontSize: "10px",
                      fontWeight: "bold",
                      background: `${getStatusColor(transaction.status)}22`,
                      padding: "2px 6px",
                      borderRadius: "8px",
                      border: `1px solid ${getStatusColor(
                        transaction.status
                      )}33`,
                    }}
                  >
                    {getStatusText(transaction.status)}
                  </div>
                </div>
              </div>

              {/* Notes */}
              {transaction.notes && (
                <div
                  style={{
                    marginTop: "10px",
                    padding: "8px",
                    background: "#ffffff11",
                    borderRadius: "6px",
                    color: "#cccccc",
                    fontSize: "11px",
                  }}
                >
                  หมายเหตุ: {transaction.notes}
                </div>
              )}
            </div>
          ))}

          {/* Load More Button */}
          {transactions.length < total && (
            <MaxButton
              title={loading ? "กำลังโหลด..." : "โหลดเพิ่ม"}
              onClick={() => loadTransactions(false)}
              className="margin-top-20"
              status={StatusMaxButton.back}
              loading={loading}
            />
          )}
        </div>

        {/* Contact Section */}
        <div
          className="container"
          style={{
            marginTop: "30px",
            padding: "15px",
            background: "linear-gradient(135deg, #ffbe0011 0%, #ffbe0022 100%)",
            borderRadius: "12px",
            border: "1px solid #ffbe0033",
          }}
        >
          <div className="text-center">
            <div
              style={{
                fontSize: "18px",
                marginBottom: "10px",
                color: "#ffbe00",
                fontWeight: "bold",
              }}
            >
              ติดต่อเรา
            </div>
            <p
              style={{
                color: "#ffffff",
                fontSize: "14px",
                margin: "0 0 10px 0",
              }}
            >
              <strong>พบปัญหาหรือต้องการความช่วยเหลือ?</strong>
            </p>
            <p
              style={{
                color: "#cccccc",
                fontSize: "12px",
                marginBottom: "15px",
              }}
            >
              ติดต่อเจ้าหน้าที่ผ่านช่องทางแชทได้ตลอด 24 ชั่วโมง
            </p>
            {router.pathname.includes("contact") === false && (
              <div>
                {lineid != null ? (
                  <Link href={lineid}>
                    <a
                      style={{
                        display: "inline-block",
                        background:
                          "linear-gradient(135deg, #de006f 0%, #f73736 100%)",
                        color: "#ffffff",
                        padding: "8px 20px",
                        borderRadius: "20px",
                        textDecoration: "none",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      แชทกับเจ้าหน้าที่
                    </a>
                  </Link>
                ) : (
                  <Link href={`/contact/${prefix}`}>
                    <a
                      style={{
                        display: "inline-block",
                        background:
                          "linear-gradient(135deg, #de006f 0%, #f73736 100%)",
                        color: "#ffffff",
                        padding: "8px 20px",
                        borderRadius: "20px",
                        textDecoration: "none",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      แชทกับเจ้าหน้าที่
                    </a>
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </LayoutMaster>
  );
}

export const getServerSideProps: GetServerSideProps<any> = async (context) => {
  const { req } = context;
  console.log("COTEXT =>", req.headers.host);
  console.log("query =>", context.query);
  const infomation = CheckWithPrefix({ host: req.headers.host as string });

  return {
    props: {
      isLogin: context.req.cookies.access_token != undefined,
      infomation: infomation,
    },
  };
};

const mapStateToProps = (state: AppReducerStateProps) => {
  return {
    state: state,
  };
};

export default connect(mapStateToProps)(TransactionTransferV2);
